import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  FaSpinner,
  FaSync,
  FaExpand,
  FaEye,
  FaEyeSlash,
  FaPlus,
  FaTimes,
  FaCloudUploadAlt,
} from 'react-icons/fa';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

const RC_PARAM = 'popular_sign_v2';
const EXTRA_IDS_STORAGE_KEY = 'featured-coverage-extra-sign-ids';
const REMOVED_IDS_STORAGE_KEY = 'featured-coverage-removed-sign-ids';
const RADIUS_MILES = 200;
const RADIUS_METERS = RADIUS_MILES * 1609.344;

const DefaultIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41],
});
L.Marker.prototype.options.icon = DefaultIcon;

const CIRCLE_STYLE = {
  color: '#0b7fd4',
  weight: 2,
  fillColor: '#0b7fd4',
  fillOpacity: 0.12,
};

function FitBounds({ bounds, trigger }) {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.isValid()) {
      map.fitBounds(bounds, { padding: [48, 48], maxZoom: 6 });
    }
  }, [map, bounds, trigger]);
  return null;
}

function signKey(sign) {
  return sign?.adunit_id || sign?.programmatic_platform_key || '';
}

function hasCoords(sign) {
  const lat = Number(sign?.latitude);
  const lng = Number(sign?.longitude);
  return Number.isFinite(lat) && Number.isFinite(lng);
}

function formatCoord(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(5) : '—';
}

/** Bounding box for a circle without adding a Leaflet layer to a map. */
function circleBounds(lat, lng, radiusMeters) {
  const latRad = (lat * Math.PI) / 180;
  const latOffset = radiusMeters / 111320;
  const lngOffset = radiusMeters / (111320 * Math.cos(latRad));
  return L.latLngBounds(
    [lat - latOffset, lng - lngOffset],
    [lat + latOffset, lng + lngOffset]
  );
}

function loadExtraSignIdsFromStorage() {
  try {
    const raw = localStorage.getItem(EXTRA_IDS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : [];
  } catch {
    return [];
  }
}

function saveExtraSignIdsToStorage(ids) {
  localStorage.setItem(EXTRA_IDS_STORAGE_KEY, JSON.stringify(ids));
}

function loadRemovedSignIdsFromStorage() {
  try {
    const raw = localStorage.getItem(REMOVED_IDS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(String).filter(Boolean) : [];
  } catch {
    return [];
  }
}

function saveRemovedSignIdsToStorage(ids) {
  localStorage.setItem(REMOVED_IDS_STORAGE_KEY, JSON.stringify(ids));
}

function keysEqual(a, b) {
  if (a.length !== b.length) return false;
  const norm = (list) => [...list].map((k) => k.toLowerCase()).sort();
  const sa = norm(a);
  const sb = norm(b);
  return sa.every((k, i) => k === sb[i]);
}

function buildDraftKeys(publishedKeys, extraKeys, removedKeys) {
  const removed = new Set(removedKeys.map((k) => k.toLowerCase()));
  const fromPublished = publishedKeys.filter((k) => !removed.has(k.toLowerCase()));
  return mergeSignIds(fromPublished, extraKeys);
}

/** Parse UUIDs from textarea (comma, space, or newline separated). */
function parseSignIdsInput(text) {
  const parts = String(text || '')
    .split(/[\s,;]+/)
    .map((s) => s.trim())
    .filter(Boolean);
  return [...new Set(parts)];
}

function mergeSignIds(rcKeys, extraKeys) {
  const seen = new Set();
  const merged = [];
  [...rcKeys, ...extraKeys].forEach((key) => {
    const lower = String(key).toLowerCase();
    if (!lower || seen.has(lower)) return;
    seen.add(lower);
    merged.push(key);
  });
  return merged;
}

async function fetchPopularSignV2() {
  const res = await fetch('/api/featured-signs/popular-sign-v2', {
    credentials: 'same-origin',
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json.success) {
    throw new Error(json.error || `Failed to load ${RC_PARAM} (${res.status})`);
  }
  return Array.isArray(json.value) ? json.value : [];
}

async function publishPopularSignV2(items) {
  const res = await fetch('/api/featured-signs/popular-sign-v2', {
    method: 'PUT',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ items }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || !json.success) {
    throw new Error(json.error || `Failed to publish (${res.status})`);
  }
  return json.value || items;
}

async function fetchSignsByPlatformKeys(platformKeys) {
  const res = await fetch('/api/signs/by-platform-keys', {
    method: 'POST',
    credentials: 'same-origin',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ platformKeys }),
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(json.error || `Failed to load signs (${res.status})`);
  }
  return json.data || json.signs || [];
}

const STATUS_LABELS = {
  ok: { label: 'On map', className: 'bg-emerald-100 text-emerald-800' },
  missing_coords: { label: 'Missing coordinates', className: 'bg-rose-100 text-rose-800' },
  not_in_inventory: { label: 'Not in inventory', className: 'bg-amber-100 text-amber-800' },
};

const SOURCE_LABELS = {
  remote_config: { label: 'Featured (RC)', className: 'bg-sky-100 text-sky-800' },
  manual: { label: 'Added', className: 'bg-violet-100 text-violet-800' },
};

const FeaturedSignsCoverageMap = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [platformKeys, setPlatformKeys] = useState([]);
  const [tableRows, setTableRows] = useState([]);
  const [signs, setSigns] = useState([]);
  const [notInInventory, setNotInInventory] = useState([]);
  const [missingCoords, setMissingCoords] = useState([]);
  const [showCircles, setShowCircles] = useState(true);
  const [fitTrigger, setFitTrigger] = useState(0);
  const [publishedKeys, setPublishedKeys] = useState([]);
  const [extraSignIds, setExtraSignIds] = useState(loadExtraSignIdsFromStorage);
  const [removedKeys, setRemovedKeys] = useState(loadRemovedSignIdsFromStorage);
  const [signIdInput, setSignIdInput] = useState('');
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState(null);

  const draftKeys = useMemo(
    () => buildDraftKeys(publishedKeys, extraSignIds, removedKeys),
    [publishedKeys, extraSignIds, removedKeys]
  );

  const hasUnpublishedChanges = useMemo(
    () => !keysEqual(publishedKeys, draftKeys),
    [publishedKeys, draftKeys]
  );

  const resolveKeys = useCallback(async (keys, publishedKeySet) => {
    if (keys.length === 0) {
      setSigns([]);
      setTableRows([]);
      setNotInInventory([]);
      setMissingCoords([]);
      return;
    }

    const signsData = await fetchSignsByPlatformKeys(keys);

    const byKey = new Map();
    signsData.forEach((s) => {
      const k = signKey(s);
      if (k) byKey.set(k.toLowerCase(), s);
    });

    const resolved = [];
    const missing = [];
    const noCoords = [];
    const rows = [];

    keys.forEach((key) => {
      const source = publishedKeySet.has(key.toLowerCase()) ? 'remote_config' : 'manual';
      const sign = byKey.get(key.toLowerCase());
      if (!sign) {
        missing.push(key);
        rows.push({ key, status: 'not_in_inventory', sign: null, source });
        return;
      }
      if (!hasCoords(sign)) {
        noCoords.push({ key, sign });
        rows.push({ key, status: 'missing_coords', sign, source });
        return;
      }
      resolved.push(sign);
      rows.push({ key, status: 'ok', sign, source });
    });

    setTableRows(rows);
    setSigns(resolved);
    setNotInInventory(missing);
    setMissingCoords(noCoords);
    setFitTrigger((t) => t + 1);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNotInInventory([]);
    setMissingCoords([]);

    try {
      const items = await fetchPopularSignV2();
      const fromRc = items.map((i) => i.adunitId || i.adunit_id).filter(Boolean);
      setPublishedKeys(fromRc);

      const publishedSet = new Set(fromRc.map((k) => k.toLowerCase()));
      const keys = buildDraftKeys(fromRc, extraSignIds, removedKeys);
      setPlatformKeys(keys);

      await resolveKeys(keys, publishedSet);
    } catch (err) {
      console.error('FeaturedSignsCoverageMap load error:', err);
      setError(err.message || 'Failed to load coverage data');
      setSigns([]);
      setTableRows([]);
      setPlatformKeys([]);
      setPublishedKeys([]);
    } finally {
      setLoading(false);
    }
  }, [extraSignIds, removedKeys, resolveKeys]);

  const refreshDraftOnMap = useCallback(
    async (nextPublished, nextExtra, nextRemoved) => {
      const published = nextPublished ?? publishedKeys;
      const extra = nextExtra ?? extraSignIds;
      const removed = nextRemoved ?? removedKeys;
      const publishedSet = new Set(published.map((k) => k.toLowerCase()));
      const keys = buildDraftKeys(published, extra, removed);
      setPlatformKeys(keys);
      setLoading(true);
      setError(null);
      try {
        await resolveKeys(keys, publishedSet);
      } catch (err) {
        setError(err.message || 'Failed to load signs');
      } finally {
        setLoading(false);
      }
    },
    [publishedKeys, extraSignIds, removedKeys, resolveKeys]
  );

  const handleAddSignIds = async () => {
    const parsed = parseSignIdsInput(signIdInput);
    if (!parsed.length) return;

    const nextExtra = mergeSignIds(extraSignIds, parsed);
    const nextRemoved = removedKeys.filter(
      (id) => !parsed.some((p) => p.toLowerCase() === id.toLowerCase())
    );
    setExtraSignIds(nextExtra);
    setRemovedKeys(nextRemoved);
    saveExtraSignIdsToStorage(nextExtra);
    saveRemovedSignIdsToStorage(nextRemoved);
    setSignIdInput('');
    setPublishSuccess(null);
    await refreshDraftOnMap(publishedKeys, nextExtra, nextRemoved);
  };

  const handleRemoveFromDraft = async (id) => {
    const lower = id.toLowerCase();
    let nextExtra = extraSignIds;
    let nextRemoved = removedKeys;

    if (extraSignIds.some((x) => x.toLowerCase() === lower)) {
      nextExtra = extraSignIds.filter((x) => x.toLowerCase() !== lower);
      setExtraSignIds(nextExtra);
      saveExtraSignIdsToStorage(nextExtra);
    } else if (publishedKeys.some((x) => x.toLowerCase() === lower)) {
      nextRemoved = mergeSignIds(removedKeys, [id]);
      setRemovedKeys(nextRemoved);
      saveRemovedSignIdsToStorage(nextRemoved);
    }

    setPublishSuccess(null);
    await refreshDraftOnMap(publishedKeys, nextExtra, nextRemoved);
  };

  const handleClearLocalEdits = async () => {
    setExtraSignIds([]);
    setRemovedKeys([]);
    saveExtraSignIdsToStorage([]);
    saveRemovedSignIdsToStorage([]);
    setSignIdInput('');
    setPublishSuccess(null);
    await refreshDraftOnMap(publishedKeys, [], []);
  };

  const handlePublishToFirebase = async () => {
    if (!draftKeys.length) {
      setError('Add at least one sign ID before publishing.');
      return;
    }

    const confirmMsg = `Publish ${draftKeys.length} sign(s) to Firebase Remote Config (${RC_PARAM})?\n\nThis updates the live mobile app config.`;
    if (!window.confirm(confirmMsg)) return;

    setPublishing(true);
    setError(null);
    setPublishSuccess(null);

    try {
      const items = draftKeys.map((adunitId) => ({ adunitId }));
      const published = await publishPopularSignV2(items);
      const newKeys = published.map((i) => i.adunitId || i.adunit_id).filter(Boolean);

      setPublishedKeys(newKeys);
      setExtraSignIds([]);
      setRemovedKeys([]);
      saveExtraSignIdsToStorage([]);
      saveRemovedSignIdsToStorage([]);
      setPlatformKeys(newKeys);

      const publishedSet = new Set(newKeys.map((k) => k.toLowerCase()));
      await resolveKeys(newKeys, publishedSet);
      setPublishSuccess(`Published ${newKeys.length} featured sign(s) to Firebase.`);
    } catch (err) {
      console.error('Publish error:', err);
      setError(err.message || 'Failed to publish to Firebase');
    } finally {
      setPublishing(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  const mapBounds = useMemo(() => {
    if (!signs.length) return null;
    const bounds = L.latLngBounds([]);
    signs.forEach((sign) => {
      const lat = Number(sign.latitude);
      const lng = Number(sign.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
      bounds.extend(circleBounds(lat, lng, RADIUS_METERS));
    });
    return bounds.isValid() ? bounds : null;
  }, [signs]);

  const defaultCenter = useMemo(() => {
    if (signs.length) {
      const lat =
        signs.reduce((sum, s) => sum + Number(s.latitude), 0) / signs.length;
      const lng =
        signs.reduce((sum, s) => sum + Number(s.longitude), 0) / signs.length;
      return [lat, lng];
    }
    return [39.8283, -98.5795];
  }, [signs]);

  const handleFitMap = () => setFitTrigger((t) => t + 1);

  return (
    <div className="flex min-h-0 flex-col">
      <header className="shrink-0 border-b border-slate-200 bg-white px-4 py-4 md:px-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">
              Featured signs coverage ({RADIUS_MILES} mi)
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Pins and radius circles from Remote Config <code className="text-xs">{RC_PARAM}</code>.
              Gaps between circles are uncovered areas.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              onClick={loadData}
              disabled={loading}
            >
              {loading ? <FaSpinner className="animate-spin" /> : <FaSync />}
              Refresh
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              onClick={handleFitMap}
              disabled={!signs.length}
            >
              <FaExpand />
              Fit map
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              onClick={() => setShowCircles((v) => !v)}
            >
              {showCircles ? <FaEyeSlash /> : <FaEye />}
              {showCircles ? 'Hide circles' : 'Show circles'}
            </button>
            <button
              type="button"
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
              onClick={handlePublishToFirebase}
              disabled={loading || publishing || !hasUnpublishedChanges}
              title={
                hasUnpublishedChanges
                  ? 'Publish draft to Firebase Remote Config'
                  : 'No changes to publish'
              }
            >
              {publishing ? <FaSpinner className="animate-spin" /> : <FaCloudUploadAlt />}
              Publish to Firebase
            </button>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 rounded-lg border border-violet-200 bg-violet-50 px-3 py-1.5 text-sm font-medium text-violet-800 hover:bg-violet-100"
            onClick={() => setShowAddPanel((v) => !v)}
          >
            <FaPlus className="text-xs" />
            {showAddPanel ? 'Hide add signs' : 'Add sign IDs'}
          </button>
          {hasUnpublishedChanges && (
            <button
              type="button"
              className="text-sm text-slate-500 underline hover:text-slate-800"
              onClick={handleClearLocalEdits}
              disabled={loading || publishing}
            >
              Discard local edits
            </button>
          )}
        </div>

        {showAddPanel && (
          <div className="mt-3 rounded-lg border border-violet-200 bg-violet-50/50 p-3">
            <p className="text-sm text-slate-700">
              Paste ad unit / platform keys to preview on the map. Use{' '}
              <strong>Publish to Firebase</strong> to save to Remote Config{' '}
              <code className="text-xs">{RC_PARAM}</code>.
            </p>
            <textarea
              className="mt-2 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-xs text-slate-800 placeholder:text-slate-400"
              rows={3}
              placeholder={'One UUID per line, or comma-separated\n75b1b3e0-699e-4735-97b8-24bdfbf78eac'}
              value={signIdInput}
              onChange={(e) => setSignIdInput(e.target.value)}
            />
            <div className="mt-2 flex flex-wrap gap-2">
              <button
                type="button"
                className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-violet-700 disabled:opacity-50"
                onClick={handleAddSignIds}
                disabled={loading || !signIdInput.trim()}
              >
                <FaPlus />
                Add & update map
              </button>
            </div>
            {extraSignIds.length > 0 && (
              <ul className="mt-3 flex flex-wrap gap-2">
                {extraSignIds.map((id) => (
                  <li
                    key={id}
                    className="inline-flex items-center gap-1 rounded-full border border-violet-200 bg-white py-0.5 pl-2 pr-1 font-mono text-xs text-violet-900"
                  >
                    <span className="max-w-[200px] truncate">{id}</span>
                    <button
                      type="button"
                      className="rounded-full p-0.5 text-violet-500 hover:bg-violet-100 hover:text-violet-800"
                      onClick={() => handleRemoveFromDraft(id)}
                      aria-label={`Remove ${id}`}
                      disabled={loading || publishing}
                    >
                      <FaTimes />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="mt-3 flex flex-wrap gap-4 text-sm">
          <span>
            <strong className="text-slate-700">Published:</strong> {publishedKeys.length}
          </span>
          <span>
            <strong className="text-slate-700">Draft:</strong> {draftKeys.length}
          </span>
          {hasUnpublishedChanges && (
            <span className="font-medium text-amber-700">Unpublished changes</span>
          )}
          <span>
            <strong className="text-slate-700">On map:</strong> {signs.length}
          </span>
          <span>
            <strong className="text-amber-700">Not in inventory:</strong>{' '}
            {notInInventory.length}
          </span>
          <span>
            <strong className="text-rose-700">Missing coordinates:</strong>{' '}
            {missingCoords.length}
          </span>
        </div>

        {publishSuccess && (
          <div className="mt-3 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
            {publishSuccess}
          </div>
        )}

        {error && (
          <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
            {error}
          </div>
        )}

        {(notInInventory.length > 0 || missingCoords.length > 0) && (
          <div className="mt-3 grid gap-2 text-sm md:grid-cols-2">
            {notInInventory.length > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                <p className="font-medium text-amber-900">Not in inventory</p>
                <ul className="mt-1 list-inside list-disc font-mono text-xs text-amber-800">
                  {notInInventory.map((id) => (
                    <li key={id}>{id}</li>
                  ))}
                </ul>
              </div>
            )}
            {missingCoords.length > 0 && (
              <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2">
                <p className="font-medium text-rose-900">Missing coordinates</p>
                <ul className="mt-1 list-inside list-disc font-mono text-xs text-rose-800">
                  {missingCoords.map(({ key }) => (
                    <li key={key}>{key}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </header>

      <div className="relative h-[min(52vh,520px)] min-h-[280px] shrink-0">
        {loading && (
          <div className="absolute inset-0 z-[500] flex items-center justify-center bg-white/80">
            <FaSpinner className="animate-spin text-3xl text-sky-600" />
          </div>
        )}

        {!loading && signs.length === 0 && (
          <div className="flex h-full items-center justify-center p-8 text-center text-slate-500">
            {error
              ? 'Fix the error above and refresh.'
              : 'No signs with coordinates to display. Check Remote Config and the signs API.'}
          </div>
        )}

        {signs.length > 0 && (
          <MapContainer
            center={defaultCenter}
            zoom={4}
            className="h-full w-full"
            scrollWheelZoom
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {mapBounds && <FitBounds bounds={mapBounds} trigger={fitTrigger} />}
            {signs.map((sign) => {
              const lat = Number(sign.latitude);
              const lng = Number(sign.longitude);
              const id = signKey(sign);
              return (
                <React.Fragment key={id}>
                  {showCircles && (
                    <Circle
                      center={[lat, lng]}
                      radius={RADIUS_METERS}
                      pathOptions={CIRCLE_STYLE}
                    />
                  )}
                  <Marker position={[lat, lng]}>
                    <Popup>
                      <div className="text-sm">
                        <strong>{sign.asset_name || 'Sign'}</strong>
                        <br />
                        {[sign.city, sign.state].filter(Boolean).join(', ')}
                        <br />
                        <span className="font-mono text-xs text-slate-600">{id}</span>
                      </div>
                    </Popup>
                  </Marker>
                </React.Fragment>
              );
            })}
          </MapContainer>
        )}
      </div>

      <section className="shrink-0 border-t border-slate-200 bg-white px-4 py-4 md:px-6">
        <h2 className="text-base font-semibold text-slate-900">All featured signs</h2>
        <p className="mt-0.5 text-sm text-slate-500">
          Featured signs from Remote Config plus any IDs you added locally.
        </p>

        {tableRows.length === 0 && !loading ? (
          <p className="mt-4 text-sm text-slate-500">No signs configured.</p>
        ) : (
          <div className="mt-3 overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200 text-left text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2.5 font-semibold text-slate-700">#</th>
                  <th className="px-3 py-2.5 font-semibold text-slate-700">Source</th>
                  <th className="px-3 py-2.5 font-semibold text-slate-700">Status</th>
                  <th className="px-3 py-2.5 font-semibold text-slate-700">Asset name</th>
                  <th className="px-3 py-2.5 font-semibold text-slate-700">City</th>
                  <th className="px-3 py-2.5 font-semibold text-slate-700">State</th>
                  <th className="px-3 py-2.5 font-semibold text-slate-700">Latitude</th>
                  <th className="px-3 py-2.5 font-semibold text-slate-700">Longitude</th>
                  <th className="px-3 py-2.5 font-semibold text-slate-700">Publisher</th>
                  <th className="px-3 py-2.5 font-semibold text-slate-700">Ad unit ID</th>
                  <th className="px-3 py-2.5 font-semibold text-slate-700"> </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {tableRows.map((row, index) => {
                  const { sign, key, status, source } = row;
                  const statusMeta = STATUS_LABELS[status] || STATUS_LABELS.ok;
                  const sourceMeta = SOURCE_LABELS[source] || SOURCE_LABELS.remote_config;
                  const id = sign ? signKey(sign) : key;
                  return (
                    <tr key={key} className="hover:bg-slate-50/80">
                      <td className="whitespace-nowrap px-3 py-2.5 text-slate-500">{index + 1}</td>
                      <td className="whitespace-nowrap px-3 py-2.5">
                        <span
                          className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${sourceMeta.className}`}
                        >
                          {sourceMeta.label}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5">
                        <span
                          className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${statusMeta.className}`}
                        >
                          {statusMeta.label}
                        </span>
                      </td>
                      <td className="max-w-[200px] truncate px-3 py-2.5 font-medium text-slate-900">
                        {sign?.asset_name || '—'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-slate-700">
                        {sign?.city || '—'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-slate-700">
                        {sign?.state || '—'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 font-mono text-xs text-slate-600">
                        {sign ? formatCoord(sign.latitude) : '—'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 font-mono text-xs text-slate-600">
                        {sign ? formatCoord(sign.longitude) : '—'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 text-slate-700">
                        {sign?.publisher || sign?.media_owner || '—'}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5 font-mono text-xs text-slate-600">
                        {id}
                      </td>
                      <td className="whitespace-nowrap px-3 py-2.5">
                        <button
                          type="button"
                          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-rose-600"
                          onClick={() => handleRemoveFromDraft(key)}
                          disabled={loading || publishing}
                          aria-label={`Remove ${key} from draft`}
                        >
                          <FaTimes />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default FeaturedSignsCoverageMap;
